import React, { useState } from 'react';
import WelcomePanel from './WelcomePanel';
import WelcomeStrip from './WelcomeStrip';

/**
 * Picks which of the two welcome shapes to render. The third state — the
 * person has an RFQ of their own — is not handled here at all: the dashboard
 * stops rendering this component entirely, so there is nothing to clean up
 * and nothing that can come back.
 *
 * Presentational: the dismissal timestamp and the count that gates this
 * arrive as props (`useDashboardWelcome` owns the write), so the /design
 * gallery can render every state without a backend.
 */
export interface DashboardWelcomeProps {
  firstName: string;
  activeTotal: number;
  /** True once the user has dismissed it — the stored timestamp exists. */
  dismissed: boolean;
  onStart: () => void;
  onDismiss: () => void;
  dismissing?: boolean;
  dismissFailed?: boolean;
}

export const DashboardWelcome: React.FC<DashboardWelcomeProps> = ({
  firstName,
  activeTotal,
  dismissed,
  onStart,
  onDismiss,
  dismissing = false,
  dismissFailed = false,
}) => {
  // Re-opening from the strip is session-only: the dismissal is a record of
  // something the user did, so reading it again never rewrites it.
  const [reopened, setReopened] = useState(false);

  if (dismissed && !reopened) {
    return <WelcomeStrip onStart={onStart} onExpand={() => setReopened(true)} />;
  }

  return (
    <WelcomePanel
      firstName={firstName}
      activeTotal={activeTotal}
      onStart={onStart}
      onDismiss={() => {
        setReopened(false);
        onDismiss();
      }}
      dismissing={dismissing}
      dismissFailed={dismissFailed}
    />
  );
};

export default DashboardWelcome;
